import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import IncidentHeader from './components/IncidentHeader';
import IncidentDescription from './components/IncidentDescription';
import ActiveUsers from './components/ActiveUsers';
import ImpactedSystems from './components/ImpactedSystems';
import FileAttachments from './components/FileAttachments';
import ChatInterface from './components/ChatInterface';
import ActivityTimeline from './components/ActivityTimeline';
import RelatedIncidents from './components/RelatedIncidents';
import ResolutionDocumentation from './components/ResolutionDocumentation';

const IncidentDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const incidentId = searchParams?.get('id') || 'INC-2024-001';
  const [incident, setIncident] = useState({});

  useEffect(() => {
    const fetchIncident = async () => {
      const response = await fetch(`${import.meta.env.VITE_APP_SERVER_URL}/incident/${incidentId}`);
      const data = await response.json();
      const incidentData = {
        id: data.id,
        title: data.title,
        description: data.description,
        severity: data.severity,
        status: data.status,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        assignedTo: data.assignee,
        slaHoursRemaining: 6,
        detectionMethod: "Automated IDS Alert",
        source: data.source,
        category: data.category,
        subcategory: "Unauthorized Access Attempt",
        resolution: {
          summary: "Blocked malicious IP addresses and strengthened authentication protocols",
          rootCause: "Weak password policies allowed credential stuffing attacks to partially succeed",
          solution: "Implemented IP blocking, enforced MFA, and updated password policies",
          preventiveMeasures: "Enhanced monitoring rules and mandatory security training",
          lessonsLearned: "Need for proactive threat hunting and better user education",
          followUpActions: [
            { id: 1, action: "Conduct security awareness training", status: "pending" },
            { id: 2, action: "Review and update incident response procedures", status: "pending" }
          ],
          verificationSteps: "Monitored network traffic for 48 hours post-resolution with no further incidents",
          timeToResolve: "8 hours 45 minutes",
          resourcesUsed: ["SIEM Platform", "Threat Intelligence Feed", "Network Analysis Tools"],
          resolvedBy: "Sarah Chen",
          resolvedAt: "2024-09-19T17:15:00Z"
        }
      };

      setIncident(incidentData);
    };
    fetchIncident();
  }, []);

  // Mock current user data
  const currentUser = {
    id: 1,
    name: "Sarah Chen",
    email: "sarah.chen@cyberguard.gov",
    role: "Senior Security Analyst",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    permissions: ["view_incidents", "edit_incidents", "escalate_incidents", "resolve_incidents"]
  };


  // Mock active users
  const [activeUsers] = useState([
    {
      id: 1,
      name: "Sarah Chen",
      role: "Senior Security Analyst",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      presence: "active",
      lastActivity: "2 minutes ago",
      currentActivity: "reviewing system logs"
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      role: "SOC Manager",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      presence: "active",
      lastActivity: "5 minutes ago",
      currentActivity: "coordinating response team"
    },
    {
      id: 3,
      name: "Emily Johnson",
      role: "Incident Response Specialist",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      presence: "away",
      lastActivity: "15 minutes ago",
      currentActivity: null
    }
  ]);

  // Mock impacted systems
  const [impactedSystems] = useState([
    {
      id: 1,
      name: "Primary Database Cluster",
      description: "Main customer data repository",
      criticality: "Critical",
      health: "Degraded",
      type: "Database",
      environment: "Production",
      owner: "Database Team",
      dependencies: ["Authentication Service", "Backup Systems", "Monitoring Tools"],
      impactDetails: "Performance degraded by 30% due to increased security monitoring"
    },
    {
      id: 2,
      name: "Authentication Server",
      description: "Primary user authentication system",
      criticality: "High",
      health: "Healthy",
      type: "Authentication",
      environment: "Production",
      owner: "Security Team",
      dependencies: ["LDAP Directory", "Certificate Authority"],
      impactDetails: "Additional security measures implemented, slight latency increase"
    },
    {
      id: 3,
      name: "Network Monitoring System",
      description: "Real-time network traffic analysis",
      criticality: "Medium",
      health: "Healthy",
      type: "Monitoring",
      environment: "Production",
      owner: "Network Team",
      dependencies: ["Log Aggregation", "Alert System"],
      impactDetails: "Increased alert volume due to enhanced monitoring rules"
    }
  ]);

  // Mock file attachments
  const [attachments, setAttachments] = useState([
    {
      id: 1,
      name: "network_analysis_report.pdf",
      type: "application/pdf",
      size: 2457600,
      uploadedBy: "Sarah Chen",
      uploadedAt: "2024-09-19T09:15:00Z",
      version: 1,
      url: "#"
    },
    {
      id: 2,
      name: "suspicious_ips_list.csv",
      type: "text/csv",
      size: 15360,
      uploadedBy: "Michael Rodriguez",
      uploadedAt: "2024-09-19T10:30:00Z",
      version: 2,
      url: "#"
    },
    {
      id: 3,
      name: "system_logs_screenshot.png",
      type: "image/png",
      size: 892160,
      uploadedBy: "Emily Johnson",
      uploadedAt: "2024-09-19T11:00:00Z",
      version: 1,
      url: "#"
    }
  ]);

  // Mock chat messages
  const [messages, setMessages] = useState([
    {
      id: 1,
      content: "I've identified the source IPs and they appear to be from a known botnet. Blocking them now.",
      sender: "Sarah Chen",
      senderId: 1,
      timestamp: new Date(Date.now() - 3600000),
      type: "text"
    },
    {
      id: 2,
      content: "Good work. I\'m coordinating with the network team to implement additional firewall rules.",
      sender: "Michael Rodriguez",
      senderId: 2,
      timestamp: new Date(Date.now() - 3300000),
      type: "text"
    },
    {
      id: 3,
      content: "System update: All malicious IPs have been blocked. Monitoring for any bypass attempts.",
      sender: "System",
      senderId: 0,
      timestamp: new Date(Date.now() - 3000000),
      type: "system"
    },
    {
      id: 4,
      content: "I\'ve uploaded the detailed analysis report. The attack pattern suggests this was automated.",
      sender: "Emily Johnson",
      senderId: 3,
      timestamp: new Date(Date.now() - 2700000),
      type: "text"
    },
    {
      id: 5,
      content: "Thanks Emily. I\'m reviewing the report now. We should consider implementing additional MFA requirements.",
      sender: "Sarah Chen",
      senderId: 1,
      timestamp: new Date(Date.now() - 1800000),
      type: "text"
    }
  ]);

  // Mock activity timeline
  const [activities, setActivities] = useState([
    {
      id: 1,
      type: "created",
      description: "Incident created",
      user: "System",
      userId: 0,
      timestamp: new Date("2024-09-19T08:30:00Z"),
      details: { title: incident?.title }
    },
    {
      id: 2,
      type: "assignment",
      description: "Incident assigned",
      user: "Michael Rodriguez",
      userId: 2,
      timestamp: new Date("2024-09-19T08:35:00Z"),
      details: { assignee: "Sarah Chen" }
    },
    {
      id: 3,
      type: "status_change",
      description: "Status updated",
      user: "Sarah Chen",
      userId: 1,
      timestamp: new Date("2024-09-19T09:00:00Z"),
      details: { from: "Open", to: "In Progress" }
    },
    {
      id: 4,
      type: "file_upload",
      description: "File uploaded",
      user: "Sarah Chen",
      userId: 1,
      timestamp: new Date("2024-09-19T09:15:00Z"),
      details: { fileName: "network_analysis_report.pdf" }
    },
    {
      id: 5,
      type: "comment",
      description: "Added investigation notes",
      user: "Sarah Chen",
      userId: 1,
      timestamp: new Date("2024-09-19T10:00:00Z"),
      details: { comment: "Initial analysis complete. Identified 15 suspicious IP addresses attempting credential stuffing attacks." }
    },
    {
      id: 6,
      type: "escalation",
      description: "Incident escalated",
      user: "Michael Rodriguez",
      userId: 2,
      timestamp: new Date("2024-09-19T10:30:00Z"),
      details: { level: "Senior Management" }
    }
  ]);

  // Mock related incidents
  const [relatedIncidents] = useState([
    {
      id: "INC-2024-002",
      title: "Failed Login Attempts on Administrative Accounts",
      severity: "High",
      status: "Resolved",
      createdAt: "2024-09-18T14:20:00Z",
      assignedTo: "Emily Johnson",
      relationship: "related",
      relationshipDetails: "Similar attack pattern detected 24 hours prior"
    },
    {
      id: "INC-2024-003",
      title: "Unusual Database Query Patterns",
      severity: "Medium",
      status: "In Progress",
      createdAt: "2024-09-19T10:45:00Z",
      assignedTo: "David Kim",
      relationship: "child",
      relationshipDetails: "Potential data exfiltration attempt following the breach"
    }
  ]);

  // Mock incident counts for header
  const incidentCounts = {
    critical: 2,
    high: 5,
    medium: 0,
    low: 0
  };

  // Event handlers
  const handleStatusChange = (incidentId) => {
    console.log('Status change requested for incident:', incidentId);
    // Implementation would show status change modal
  };

  const handleEscalate = (incidentId) => {
    console.log('Escalation requested for incident:', incidentId);
    // Implementation would show escalation modal
  };

  const handleAssign = (incidentId) => {
    console.log('Assignment requested for incident:', incidentId);
    // Implementation would show assignment modal
  };

  const handleUpdateIncident = (updatedIncident) => {
    setIncident(updatedIncident);
    console.log('Incident updated:', updatedIncident);
  };

  const handleFileUpload = async (file) => {
    console.log('File upload requested:', file?.name);
    // Simulate file upload
    const newAttachment = {
      id: Date.now(),
      name: file?.name,
      type: file?.type,
      size: file?.size,
      uploadedBy: currentUser?.name,
      uploadedAt: new Date()?.toISOString(),
      version: 1,
      url: "#"
    };
    setAttachments(prev => [...prev, newAttachment]);
  };

  const handleFileDownload = (fileId) => {
    console.log('File download requested:', fileId);
    // Implementation would trigger file download
  };

  const handleFileDelete = (fileId) => {
    setAttachments(prev => prev?.filter(file => file?.id !== fileId));
    console.log('File deleted:', fileId);
  };

  const handleSendMessage = (message) => {
    setMessages(prev => [...prev, message]);
    console.log('Message sent:', message);
  };

  const handleAddActivity = (activity) => {
    setActivities(prev => [activity, ...prev]);
    console.log('Activity added:', activity);
  };

  const handleLinkIncident = () => {
    console.log('Link incident requested');
    // Implementation would show incident linking modal
  };

  const handleUpdateResolution = (updatedIncident) => {
    setIncident(updatedIncident);
    console.log('Resolution updated:', updatedIncident);
  };

  // Check user permissions
  const canEdit = currentUser?.permissions?.includes('edit_incidents');
  const canEscalate = currentUser?.permissions?.includes('escalate_incidents');
  const canResolve = currentUser?.permissions?.includes('resolve_incidents');

  return (
    <div className="min-h-screen bg-background">
      <Header user={currentUser} incidentCounts={incidentCounts} />
      
      <main className="nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Incident Header */}
          <IncidentHeader
            incident={incident}
            onStatusChange={handleStatusChange}
            onEscalate={handleEscalate}
            onAssign={handleAssign}
            currentUser={currentUser}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Primary Content */}
            <div className="xl:col-span-2 space-y-6">
              <IncidentDescription
                incident={incident}
                onUpdate={handleUpdateIncident}
                canEdit={canEdit}
              />
              
              <ImpactedSystems systems={impactedSystems} />
              
              <FileAttachments
                attachments={attachments}
                onUpload={handleFileUpload}
                onDownload={handleFileDownload}
                onDelete={handleFileDelete}
                canEdit={canEdit}
              />
              
              <ActivityTimeline
                activities={activities}
                onAddActivity={handleAddActivity}
                currentUser={currentUser}
                canEdit={canEdit}
              />
              
              <RelatedIncidents
                relatedIncidents={relatedIncidents}
                onLinkIncident={handleLinkIncident}
                canEdit={canEdit}
              />
              
              <ResolutionDocumentation
                incident={incident}
                onUpdateResolution={handleUpdateResolution}
                canEdit={canResolve}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              <ActiveUsers
                activeUsers={activeUsers}
                currentUser={currentUser}
              />
              
              <ChatInterface
                messages={messages}
                onSendMessage={handleSendMessage}
                currentUser={currentUser}
                activeUsers={activeUsers}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default IncidentDetailsPage;